import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType, SettingState,
} from '@appblocks/codegeneration/types';
import * as simulator from './simulator';
import * as tios from './tios';

export default class TableItem extends BaseFeature {
    constructor() {
        super();
        this.name = 'Data Table';
        this.id = 'tbl_table';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                required: true,
                default: 'TBL',
            },
            {
                id: 'COMMENT',
                name: 'Description',
                settingType: SettingType.STRING,
                required: true,
                default: '',
                min: 0,
                max: 225,
            },
            {
                id: 'MAXRECORDS',
                name: 'Max Records',
                settingType: SettingType.NUMBER,
                required: true,
                default: 1000,
                min: 1,
                max: 65535,
            },
            {
                id: 'STRUCT',
                name: 'Table Structure',
                settingType: SettingType.OPTION,
                required: true,
                readonly: false,
                default: 'T',
                options: [
                    {
                        name: 'Log',
                        value: 'L',
                    },
                    {
                        name: 'Table',
                        value: 'T',
                    },
                ],
            },
            {
                id: 'KEYFIELDS',
                name: 'Number of Key Fields',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
                helpText: 'Number of fields at the beginning that in combination need to be unique',
                min: 0,
                max: 127,
            },
            {
                id: 'FIELDS',
                name: 'Fields',
                settingType: SettingType.ARRAY,
                refId: 'tbl_field',
                required: true,
                default: [],
            },
        ]);
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'NAME');
    }

    getDependencies() {
        const dependencies = [
            'tbl',
        ];
        return dependencies;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const fields = this.getSettingValue('FIELDS');
        if (Number(this.getSettingValue('KEYFIELDS')) > fields.length) {
            errors.push({
                featureId: this.getID(),
                settingId: 'KEYFIELDS',
                message: 'Number of Key Fields must not exceed number of fields',
                messageType: ValidationMessageType.ERROR,
            });
        }
        // check duplicate field names
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            for (let j = 0; j < fields.length; j++) {
                if (fields[i].getSettingValue('NAME') === fields[j].getSettingValue('NAME')
                    && i !== j) {
                    errors.push({
                        featureId: field.getID(),
                        settingId: 'NAME',
                        message: 'Duplicate field names',
                        messageType: ValidationMessageType.ERROR,
                    });
                }
            }
            // check field name length and only contains letters, numbers, and underscores
            const fieldName = fields[i].getSettingValue('NAME');
            if (!/^[a-zA-Z0-9_]+$/.test(fieldName)) {
                errors.push({
                    featureId: field.getID(),
                    settingId: 'NAME',
                    message: 'Field name must only contain letters, numbers, and underscores',
                    messageType: ValidationMessageType.ERROR,
                });
            }
        }
        return errors;
    }

    getState(settingId: string): SettingState {
        const mode = this.getSettingValue('MODE');
        const type = this.getSettingValue('STRUCT');
        const NAME = this.getSettingValue('NAME');
        if (settingId === 'MODE') {
            return SettingState.HIDDEN;
        }
        if (type === 'L' && (NAME === 'LOG' || NAME === 'DEBUG')) {
            switch (settingId) {
                case 'FIELDS':
                case 'NAME':
                case 'KEYFIELDS':
                    return SettingState.DISABLED;
                default:
                    break;
            }
        }
        switch (settingId) {
            case 'AGGENABLED':
            // case 'STRUCT':
                return SettingState.HIDDEN;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }
}
